/** @format */

import { ThunkDispatch } from "@reduxjs/toolkit";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import BLSpinner from "../../assets/animations/BLSpinner/BLSpinner";
import { useLocale } from "../i18n/hooks";
import ResourceUI from "./ResourceUI";
import { Resource as ResourceType } from "./types";
import { setIsEditable } from "../editable/editableSlice";
import { useGetResourceQuery } from "../../app/services/resource/resourcesApi";
import ErrorPage from "../errorPage/ErrorPage";
import { getResourceCode } from "../../utils/GetResourceCode";

const Resource: React.FC = () => {
  const locale = useLocale();
  const navigate = useNavigate();
  const dispatch = useDispatch<ThunkDispatch<any, string, any>>();
  const { pathname } = useLocation();

  const { resourceId } = useParams();

  const [resource, setResource] = useState<ResourceType>();

  const isStaff = useAppSelector((state) => state.auth.isStaff);

  // Get UserResource
  const {
    data: _resource,
    isFetching: isFetchingResource,
    error: resourceError,
  } = useGetResourceQuery(resourceId ? resourceId : skipToken);

  useEffect(() => {
    if (_resource && !isFetchingResource) {
      if (
        locale === "cy" &&
        ["eduqas", "wjec"].includes(_resource?.Branding ?? "")
      ) {
        navigate(pathname.replace(`/${locale}/`, `/en/`));
      }
      if (locale === "en" && _resource.Branding === "cbac") {
        navigate(pathname.replace(`/${locale}/`, `/cy/`));
      }

      setResource(_resource);

      // Set the resource as editable if staff member
      if (isStaff) dispatch(setIsEditable(true));
    }

    return () => {
      dispatch(setIsEditable(false));
      setResource(undefined);
    };
  }, [
    isFetchingResource,
    locale,
    navigate,
    resourceId,
    _resource,
    dispatch,
    pathname,
    isStaff,
  ]);

  // Could fetch editable status here

  useEffect(() => {
    _resource !== undefined &&
      sessionStorage.setItem("redirectResource", getResourceCode(_resource));
  }, [_resource]);

  // track the current resource's ref primary key for updating the recent resources cookie
  useEffect(() => {
    if (resource === undefined || resource.Subject === undefined) return;

    sessionStorage.setItem(
      "currentRefPK",
      `REF#SUBJECT:${resource?.Subject.id}#${locale}`.toUpperCase(),
    );
    sessionStorage.setItem(
      "currentRefSK",
      `QUALIFICATION:${resource?.Qualification}#TYPE:BL#CODE:${resourceId}`.toUpperCase(),
    );
  }, [resource, locale, resourceId]);

  return (
    <>
      {isFetchingResource ? (
        <BLSpinner
          className="position-absolute top-50 start-50 translate-middle"
          size="3rem"
        />
      ) : resource ? (
        <ResourceUI resource={resource} />
      ) : (
        resourceError && (
          <ErrorPage
            statusCode={
              "status" in resourceError &&
              typeof resourceError.status === "number"
                ? resourceError.status
                : undefined
            }
            hiddenMessage={
              "data" in resourceError && typeof resourceError.data === "string"
                ? resourceError.data
                : undefined
            }
            actions={["report", "newResource"]}
          />
        )
      )}
    </>
  );
};

export default Resource;
